import { useTranslation } from 'react-i18next';

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, FileText } from 'lucide-react';

import { CSVImportCouponCodesButton } from '../csv-import-coupon-codes-button';
import { Button } from '../ui/button';
import { BaseImportViewProps } from './import-view-props';

interface ViewCouponCodesImportProps extends BaseImportViewProps {
  dealId: string;
  downloadTemplate: () => void;
  /** Après import réussi. Défaut : `setImportView('chooser')`. */
  onCouponCodesUploadSuccess?: () => void;
  /** Flèche retour. Défaut : `setImportView('chooser')`. */
  onCouponCodesBack?: () => void;
}

export const ViewCouponCodesImport: React.FC<ViewCouponCodesImportProps> = ({
  setImportView,
  dealId,
  downloadTemplate,
  onCouponCodesUploadSuccess,
  onCouponCodesBack,
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const goToImportChooser = () => setImportView('chooser');
  const handleBack = onCouponCodesBack ?? goToImportChooser;
  const handleUploadSuccess = onCouponCodesUploadSuccess ?? goToImportChooser;

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-md p-1 hover:bg-muted"
            onClick={handleBack}
            aria-label="Revenir au choix"
          >
            {onCouponCodesBack ? null : <ArrowLeft className="h-4 w-4" />}
          </button>
          <DialogTitle>
            {t('components.import_flow.coupons_codes_import_title')}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.coupons_codes_import_message')}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-6">
        <div className="rounded-md bg-blue-50 p-4 text-xs text-blue-800">
          <p className="font-semibold">
            {t('components.import_flow.coupons_codes_import_info_title')}
          </p>
          <p className="mt-1">
            {t('components.import_flow.coupons_codes_import_info_message')}
          </p>
        </div>
        <Button
          className="w-full justify-start gap-2 border border-gray-300 bg-white text-black hover:bg-gray-50"
          onClick={downloadTemplate}
        >
          <FileText className="h-4 w-4" />
          <span className="text-sm">{t('general.download_csv_model')}</span>
        </Button>
        <CSVImportCouponCodesButton
          dealId={dealId}
          buttonText={t('components.import_flow.coupons_codes_import_title')}
          onUploadSuccess={async () => {
            toast({
              title: t(
                'components.import_flow.coupons_codes_import_toast_title'
              ),
              description: t(
                'components.import_flow.coupons_codes_import_toast_message'
              ),
            });
            handleUploadSuccess();
          }}
        />
      </div>
    </>
  );
};
